import type { CardId, RngState, Seat, Suit } from "@bing/engine-core";

/**
 * The rules this engine plays (`docs/superpowers/specs/2026-11-05-haftkhabis-rules.md`). A persisted
 * state with another version cannot be resumed: {@link "./engine".isCurrentHaftkhabisRules}.
 *
 * 2 (revision 2 of the spec): exactly 4 seats, and an 8-last's draw owes «تک کارت» (D14). A save of
 * version 1 may hold 2 or 3 seats, so it is retired, never played.
 */
export const HAFTKHABIS_RULES_VERSION = 2;

/** O: exactly 4 players, each for themselves, everywhere: public, private and solo. */
export const HAFTKHABIS_SEATS = 4;
/** O: the limits a solo player or a private host chooses from; a public table's limit is the server's. */
export const HAFTKHABIS_TARGET_SCORES = [100, 200, 300] as const;
export const DEFAULT_TARGET_SCORE = 100;
/** O: seven cards each. */
export const HAND_SIZE = 7;
/** O: a 7 lands 2, and each 7 played on it grows it by 2 (4, 6, …). */
export const PENALTY_STEP = 2;

/**
 * D16: another seat may catch only from this long after the descent. THE ENGINE HAS NO CLOCK: this is
 * the SERVER DOOR's rule (see the README), restated here so the door and the web read one number.
 */
export const CATCH_GRACE_MS = 2000;
/** D17: how long after an unarmed descent the next bot's step is held: a value in this range. */
export const CATCH_HOLD_MIN_MS = 3000;
export const CATCH_HOLD_MAX_MS = 6000;
/** How many events a state keeps: a tail, so a saved game and a frame stay small. */
export const EVENT_TAIL = 24;
/**
 * - `play`: a seat's turn has begun. Under a penalty: 7s or `draw` (pays it all). Otherwise a matching
 *   card; `draw` only when none matches (D4).
 * - `bonus`: an 8 was just played by the seat on turn, which must play again (D7), or `draw` if it can't.
 *   With an empty hand and truly nothing to draw, that `draw` ends the round, the seat its winner (D9).
 * - `drawn`: the card a normal or a bonus draw just gave can be played: `play` it, or `pass` (O, D8).
 * - `take`: a 2 was played; the seat on turn is its TARGET, which owes one `take` (D13).
 * - `gameOver`: a seat reached the limit; `totalScores` are final.
 */
export type HaftkhabisPhase = "play" | "bonus" | "drawn" | "take" | "gameOver";

/** The way the turn runs: seat+1 by default (D27), the other way after an odd number of 10s. */
export type HaftkhabisDirection = 1 | -1;

/**
 * A seat that owes «تک کارت». `seq` names the descent (its play event, or the `draw` of D14's 8-last), so a
 * NEW descent reads new.
 */
export interface HaftkhabisOwe {
  readonly seat: Seat;
  readonly seq: number;
}

/** What a finished round did. Public. */
export interface HaftkhabisRoundResult {
  readonly round: number;
  readonly winner: Seat;
  /** The card the winner went out on: never an 8, but for D9's corner (nothing to draw). */
  readonly card: CardId;
  /** The winner's last card was a Jack: everybody's points for this round were doubled. */
  readonly doubled: boolean;
  /** Per seat: this round's minus points (the winner's 0). */
  readonly points: readonly number[];
  /** Per seat: the match totals after adding `points`. */
  readonly totalsAfter: readonly number[];
  /**
   * Per seat: the cards it still held when the round ended, sorted (the winner's none). Public: that
   * round is over, so these are never the cards of the round in progress. The round's card draws them.
   */
  readonly hands: readonly (readonly CardId[])[];
}

/** Every effect a client shows once, in order. `cards`/`card` only reach the seats that may see them. */
export type HaftkhabisEventBody =
  | {
      readonly kind: "roundStart";
      readonly round: number;
      readonly dealer: Seat;
      /** The seat after the dealer (D2). */
      readonly first: Seat;
      readonly top: CardId;
      /** D1: the specials turned up and put back into the pile, in order. Public: they were face up. */
      readonly turnedBack: readonly CardId[];
    }
  | { readonly kind: "play"; readonly seat: Seat; readonly card: CardId }
  /** A 7 landed; `penalty` is the chain's value now (2, 4, 6, …). */
  | { readonly kind: "seven"; readonly seat: Seat; readonly penalty: number }
  /** `seat` paid the penalty: `owed` is what it was, `count` what the pile and discards gave (D3). */
  | {
      readonly kind: "penaltyPaid";
      readonly seat: Seat;
      readonly owed: number;
      readonly count: number;
      /** Only in `seat`'s own view. */
      readonly cards?: readonly CardId[];
    }
  /** An 8 was played: `seat` owes the bonus. */
  | { readonly kind: "bonus"; readonly seat: Seat }
  /** An Ace was played by `seat`; `skipped` loses the turn. */
  | { readonly kind: "skip"; readonly seat: Seat; readonly skipped: Seat }
  | { readonly kind: "reverse"; readonly seat: Seat; readonly direction: HaftkhabisDirection }
  /** A Jack named `suit`. */
  | { readonly kind: "suit"; readonly seat: Seat; readonly suit: Suit }
  /** A 2 named `target`: a blind take follows (`via: "take"`), or it drew from the pile (`"draw"`). */
  | {
      readonly kind: "target";
      readonly seat: Seat;
      readonly target: Seat;
      readonly via: "take" | "draw";
    }
  /** `target` took a card from `giver` (D13). */
  | {
      readonly kind: "take";
      readonly giver: Seat;
      readonly target: Seat;
      /** Only in the views of `giver` and `target`. */
      readonly card?: CardId;
    }
  /** `seat` drew; `count` may be 0 (D3). `reason`: its own turn, its 8's bonus, or a 2's one-card branch. */
  | {
      readonly kind: "draw";
      readonly seat: Seat;
      readonly count: number;
      readonly reason: "turn" | "bonus" | "two";
      /** Only in `seat`'s own view. */
      readonly cards?: readonly CardId[];
    }
  /** `seat` declined to play the card it drew. */
  | { readonly kind: "pass"; readonly seat: Seat }
  /** `seat` pressed «تک کارت»: out of turn, or carried on its play. */
  | { readonly kind: "press"; readonly seat: Seat }
  /** `by` caught `target`, which drew `count` (0 when nothing can be drawn: no penalty, D16). */
  | {
      readonly kind: "catch";
      readonly by: Seat;
      readonly target: Seat;
      readonly count: number;
      /** Only in `target`'s own view. */
      readonly cards?: readonly CardId[];
    }
  /** D3: the discards under the top became a new pile of `count` cards. */
  | { readonly kind: "reshuffle"; readonly count: number }
  | {
      readonly kind: "roundEnd";
      readonly round: number;
      readonly winner: Seat;
      readonly card: CardId;
      readonly doubled: boolean;
      readonly points: readonly number[];
      readonly totals: readonly number[];
      /** The limit was reached: no next round. */
      readonly matchOver: boolean;
    };

/** One event: `seq` grows by 1 over the whole match; a client shows those above the last it showed. */
export type HaftkhabisEvent = { readonly seq: number } & HaftkhabisEventBody;

/** Full authoritative state: a match of rounds. Pure JSON, so a saved game resumes bit for bit. */
export interface HaftkhabisState {
  /** {@link HAFTKHABIS_RULES_VERSION}. */
  readonly rulesVersion: number;
  readonly seats: number;
  readonly phase: HaftkhabisPhase;
  /** 0-based. */
  readonly round: number;
  /** D2: random in round 0, then the seat after it each round. */
  readonly dealer: Seat;
  readonly direction: HaftkhabisDirection;
  /** The ONE seat that must act now: the target during `take`. */
  readonly turn: Seat;
  readonly hands: readonly (readonly CardId[])[];
  /** Face down, drawn off the front. Hidden: its size is all a view carries. */
  readonly pile: readonly CardId[];
  /** Under {@link top}, oldest first. Every one was played face up. */
  readonly discard: readonly CardId[];
  readonly top: CardId;
  /** The suit to match: `top`'s, or the one its Jack named (D6). */
  readonly activeSuit: Suit;
  /** The 7 penalty the seat on turn faces; 0 when none. */
  readonly penalty: number;
  /** Phase `drawn`: the card that may be played. Hidden from everyone but the drawer. */
  readonly drawnCard: CardId | null;
  /** Phase `take`. */
  readonly pendingTake: { readonly giver: Seat; readonly target: Seat } | null;
  /** Who owes «تک کارت» right now: more than one can (D14, D16). */
  readonly owes: readonly HaftkhabisOwe[];
  /** Who pressed and still holds the card they pressed on. */
  readonly safe: readonly Seat[];
  /** D17: the hold {@link "./engine".haftkhabisEngine}'s `postMoveDelayMs` answers while someone owes. */
  readonly catchHoldMs: number;
  /** On-turn moves applied this round: 0 means the round's opening is on screen. */
  readonly moveCount: number;
  /** Per seat, the match's minus points: they only grow (D20). */
  readonly totalScores: readonly number[];
  /** The limit: the match ends when a total reaches it (`>=`). */
  readonly targetScore: number;
  /** The last finished round; `null` until round 0 ends. */
  readonly lastRound: HaftkhabisRoundResult | null;
  /** The newest {@link EVENT_TAIL} events, oldest first. */
  readonly events: readonly HaftkhabisEvent[];
  /** The seq of the newest event. */
  readonly eventSeq: number;
  readonly rngState: RngState;
}

/** Out-of-turn moves, from ANY seat at any time (README: the side door). */
export type HaftkhabisSideMove =
  /** You owe: this clears it (D15). */
  | { readonly type: "press" }
  /** `target` owes and is not you: it draws one (D16). The 2 s grace is the server door's. */
  | { readonly type: "catch"; readonly target: Seat };

/**
 * A `play`. `suit` and `target` RIDE the move (D21), so neither is a phase and neither costs a window.
 * - `suit`: required on a Jack, unless it is your last card (D10: no suit is named).
 * - `target`: required on a 2, unless it is your last card (D10).
 * - `tak: true`: the press carried on the play (D15), meaningful when the play leaves exactly one card.
 */
export interface HaftkhabisPlay {
  readonly type: "play";
  readonly card: CardId;
  readonly suit?: Suit;
  readonly target?: Seat;
  readonly tak?: boolean;
}

/**
 * What `applyMove` takes from the seat on turn. `tak: true` on a `draw` is the press carried on the draw
 * that makes you owe (D14: your 8 was your last card); on any other draw it means nothing. `index` on a
 * `take` is the tapped position: cosmetic, ignored, so a sorted hand can never leak through a position
 * (D13). `catch` is for a bot's own step and for nobody else (D17); `press` is never legal here: it goes
 * through the side door.
 */
export type HaftkhabisMove =
  | HaftkhabisPlay
  | { readonly type: "draw"; readonly tak?: boolean }
  | { readonly type: "pass" }
  | { readonly type: "take"; readonly index?: number }
  | HaftkhabisSideMove;

/** One seat's redacted projection: everything the table shows at a glance. README: field by field. */
export interface HaftkhabisView {
  readonly seats: number;
  readonly phase: HaftkhabisPhase;
  readonly you: Seat;
  readonly round: number;
  readonly dealer: Seat;
  readonly direction: HaftkhabisDirection;
  readonly turn: Seat;
  /** Your own cards. */
  readonly hand: readonly CardId[];
  readonly handCounts: readonly number[];
  readonly top: CardId;
  readonly activeSuit: Suit;
  readonly penalty: number;
  readonly pileCount: number;
  readonly discardCount: number;
  /** The cards under the top, SORTED: all were played face up; their order is never exposed. */
  readonly discard: readonly CardId[];
  /** Phase `drawn` and you are the drawer: the card you may play. `null` for everyone else. */
  readonly drawnCard: CardId | null;
  /** Phase `take`: who takes from whom. */
  readonly pendingTake: { readonly giver: Seat; readonly target: Seat } | null;
  readonly owes: readonly HaftkhabisOwe[];
  readonly safe: readonly Seat[];
  readonly moveCount: number;
  /** Per seat. The «ادامه بازی» strip's `summarizeView` reads this key. */
  readonly totalScores: readonly number[];
  readonly targetScore: number;
  readonly lastRound: HaftkhabisRoundResult | null;
  readonly events: readonly HaftkhabisEvent[];
  readonly eventSeq: number;
}

/** `ScoreResult.detail`. */
export interface HaftkhabisScoreDetail {
  readonly roundsPlayed: number;
  readonly targetScore: number;
  /** The record of it: the hands are the table's, and stay off a row that is kept. */
  readonly lastRound: Omit<HaftkhabisRoundResult, "hands"> | null;
}
